import { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import stringUtils from '../../utils/string';
import { hasChangedForms } from './selectors';
import { DirtyFormProxyReactContext } from './const';

const ACTION_NAMES = {
	addFormName: 'ADD_FORM_NAME_FOR_OBSERVATION',
	removeFormName: 'REMOVE_FORM_NAME_FOR_OBSERVATION',
	clearAllFormNames: 'CLEAR_ALL_FORM_NAMES_FOR_OBSERVATION',
};

const reducer = (state, action) => {
	switch (action.type) {
		case ACTION_NAMES.addFormName:
			const existFormNameIndex = state.findIndex((formName) => formName === action.payload);

			if (existFormNameIndex !== -1 || stringUtils.isNullOrEmpty(action.payload)) {
				return state;
			}

			return [...state, action.payload];
		case ACTION_NAMES.removeFormName:
			const updatedFormNames = state.filter((formName) => formName !== action.payload);

			return [...updatedFormNames];
		case ACTION_NAMES.clearAllFormNames:
			return [];
		default:
			return state;
	}
};

export const useFormNameForObservation = () => {
	const [formNamesForObservation, dispatch] = useReducer(reducer, []);

	const addFormNameForObservation = useCallback((formName) => {
		dispatch({
			type: ACTION_NAMES.addFormName,
			payload: formName,
		});
	}, []);

	const removeFormNameForObservation = useCallback((formName) => {
		dispatch({
			type: ACTION_NAMES.removeFormName,
			payload: formName,
		});
	}, []);

	const clearFormNameForObservation = useCallback(() => {
		dispatch({
			type: ACTION_NAMES.clearAllFormNames,
		});
	}, []);

	return {
		formNamesForObservation,
		addFormNameForObservation,
		clearFormNameForObservation,
		removeFormNameForObservation,
	};
};

export const useBeforeunload = (show) => {
	useEffect(() => {
		const handleBeforeunload = (event) => {
			event.preventDefault();
			event.returnValue = !show;
		};

		if (show) {
			window.addEventListener('beforeunload', handleBeforeunload);
		}

		return () => {
			if (show) {
				window.removeEventListener('beforeunload', handleBeforeunload);
			}
		};
	}, [show]);
};

export const useChangeFormHandler = () => {
	const [activeDirtyFormName, setActiveDirtyFormProxyName] = useState('');
	const {
		formNamesForObservation,
		addFormNameForObservation,
		clearFormNameForObservation,
		removeFormNameForObservation,
	} = useFormNameForObservation();
	const formHasChanges = useSelector(hasChangedForms(formNamesForObservation));

	const registerActiveDirtyFormProxyName = useCallback(
		(formName) => {
			setActiveDirtyFormProxyName(formName);
			addFormNameForObservation(formName);
		},
		[setActiveDirtyFormProxyName, addFormNameForObservation],
	);

	const unRegisterActiveDirtyFormProxyName = useCallback(
		(formName) => {
			if (formName) {
				removeFormNameForObservation(formName);
			} else {
				clearFormNameForObservation();
			}

			setActiveDirtyFormProxyName('');
		},
		[setActiveDirtyFormProxyName, clearFormNameForObservation, removeFormNameForObservation],
	);

	return {
		formHasChanges,
		activeDirtyFormName,
		addFormNameForObservation,
		clearFormNameForObservation,
		removeFormNameForObservation,
		registerActiveDirtyFormProxyName,
		unRegisterActiveDirtyFormProxyName,
	};
};

export const useChangePageHandler = () => {
	const [activeDirtyPageName, setActiveDirtyPageProxyName] = useState('');
	const [pageHasChanges, setIsPageHasChanges] = useState(false);

	return {
		pageHasChanges,
		activeDirtyPageName,
		setIsPageHasChanges,
		setActiveDirtyPageProxyName,
	};
};

export const useDirtyForm = ({ formName, confirmAction, isPageHasChanges }) => {
	const {
		setIsPageHasChanges,
		registerActiveDirtyFormProxyName,
		unRegisterActiveDirtyFormProxyName,
		registerActiveDirtyPageProxyName,
		unRegisterActiveDirtyPageProxyName,
	} = useContext(DirtyFormProxyReactContext);

	useEffect(() => {
		if (confirmAction) {
			registerActiveDirtyPageProxyName(formName, confirmAction);
		} else {
			registerActiveDirtyFormProxyName(formName);
		}

		return () => {
			if (confirmAction) {
				unRegisterActiveDirtyPageProxyName(formName);
			} else {
				unRegisterActiveDirtyFormProxyName(formName);
			}
		};
	}, [
		formName,
		confirmAction,
		registerActiveDirtyFormProxyName,
		unRegisterActiveDirtyFormProxyName,
		registerActiveDirtyPageProxyName,
		unRegisterActiveDirtyPageProxyName,
	]);

	useEffect(() => {
		setIsPageHasChanges(isPageHasChanges);

		return () => {
			if (isPageHasChanges) {
				setIsPageHasChanges(false);
			}
		};
	}, [isPageHasChanges, setIsPageHasChanges]);
};
